import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowDownAZ, ArrowUpAZ, Check, ChevronsUpDown, Plus, Search, Trash2, X } from 'lucide-react';
import { BRAND, CHART_SERIES } from '@/lib/brand';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/Popover';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/Select';
import { Switch } from '@/components/ui/Switch';
import { Textarea } from '@/components/ui/Textarea';
import { Sheet, SheetBody, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/Sheet';
import { BottomSheet, BottomSheetBody, BottomSheetFooter, BottomSheetHeader } from '@/components/ui/BottomSheet';
import type { ForecastSourceDef, MetricDef, TableDef } from '@/features/intelligence/dashboard/catalogTypes';
import type { ItemLite } from '@/features/intelligence/dashboard/datasets';
import { HEIGHT_PRESETS, WIDTH_PRESETS } from '@/features/intelligence/dashboard/layout';
import { displayToNatural, naturalToDisplay, unitInputSuffix } from '@/features/intelligence/dashboard/series';
import {
  CHART_TYPE_LABELS,
  FORECAST_METHOD_LABELS,
  GRID_COLUMNS,
  WIDGET_KIND_LABELS,
  isBreakdownChart,
  type ChartType,
  type ChartWidget,
  type DashboardWidget,
  type ForecastMethod,
  type ForecastWidget,
  type MetricWidget,
  type TableWidget,
  type TextWidget,
} from '@/features/intelligence/dashboard/types';
import { datasetQueryOptions } from '@/features/intelligence/dashboard/useDashboardData';
import { useDashboard } from './DashboardContext';
import { SourcePicker } from './SourcePicker';
import { WidgetBody } from './WidgetFrame';

export interface WidgetEditorProps {
  open: boolean;
  widget: DashboardWidget | null;
  isNew: boolean;
  onSave: (widget: DashboardWidget) => void;
  onRemove: (widgetId: string) => void;
  onClose: () => void;
  /** Lets the board start loading the draft's datasets so the preview fills in. */
  onDraftChange?: (draft: DashboardWidget) => void;
}

const KIND_HINTS: Record<DashboardWidget['kind'], string> = {
  metric: 'One number for the selected range, with its change against the prior period.',
  chart: 'Plot metrics over time, or break a total down by category.',
  forecast: 'Fit a model to monthly history and project it forward.',
  table: 'A ranked list — top products, open purchase orders, overdue receivables.',
  text: 'A note for context: how to read a section, who owns a number, what changed.',
};

const SERIES_COLORS: string[] = [
  ...CHART_SERIES,
  BRAND.brightMoss,
  BRAND.atmosphere,
  BRAND.freshSalmon,
];

const MAX_SERIES = 6;

const TIME_SERIES_TYPES: ChartType[] = ['line', 'area', 'bar', 'stacked', 'combo'];
const BREAKDOWN_TYPES: ChartType[] = ['donut', 'hbar'];

// ---------------------------------------------------------------------------
// Small form helpers
// ---------------------------------------------------------------------------

function Field({ label, hint, htmlFor, children }: { label: string; hint?: ReactNode; htmlFor?: string; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor} className="text-xs font-medium text-muted-foreground">
        {label}
      </Label>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

function SwitchRow({
  label,
  hint,
  checked,
  onCheckedChange,
  disabled,
}: {
  label: string;
  hint?: string | null;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label className={cn('flex items-start justify-between gap-4 py-1', disabled && 'opacity-60')}>
      <span className="min-w-0">
        <span className="block text-sm text-foreground">{label}</span>
        {hint && <span className="block text-xs text-muted-foreground">{hint}</span>}
      </span>
      <Switch checked={checked} onCheckedChange={onCheckedChange} disabled={disabled} />
    </label>
  );
}

function SectionTitle({ children }: { children: ReactNode }) {
  return <h4 className="pt-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{children}</h4>;
}

function SizeFields({ widget, onChange }: { widget: DashboardWidget; onChange: (patch: { w?: number; h?: number }) => void }) {
  const widthOptions = WIDTH_PRESETS.some((p) => p.w === widget.w) ? WIDTH_PRESETS : [...WIDTH_PRESETS, { w: widget.w, label: `${widget.w} of ${GRID_COLUMNS} columns` }];
  const heightOptions = HEIGHT_PRESETS.some((p) => p.h === widget.h) ? HEIGHT_PRESETS : [...HEIGHT_PRESETS, { h: widget.h, label: `${widget.h} rows` }];
  return (
    <div className="grid grid-cols-2 gap-3">
      <Field label="Width">
        <Select value={String(widget.w)} onValueChange={(v) => onChange({ w: Number(v) })}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {[...widthOptions].sort((a, b) => a.w - b.w).map((p) => (
              <SelectItem key={p.w} value={String(p.w)}>
                {p.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      <Field label="Height">
        <Select value={String(widget.h)} onValueChange={(v) => onChange({ h: Number(v) })}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {[...heightOptions].sort((a, b) => a.h - b.h).map((p) => (
              <SelectItem key={p.h} value={String(p.h)}>
                {p.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
    </div>
  );
}

function ColorSwatch({ color, onChange }: { color: string | undefined; onChange: (color: string | undefined) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Series colour"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-input bg-card hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <span
            className={cn('h-4 w-4 rounded-full', !color && 'border border-dashed border-muted-foreground/60')}
            style={color ? { backgroundColor: color } : undefined}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-2">
        <div className="grid grid-cols-5 gap-1.5">
          {SERIES_COLORS.map((c) => (
            <button
              key={c}
              type="button"
              aria-label={c}
              onClick={() => {
                onChange(c);
                setOpen(false);
              }}
              className={cn(
                'flex h-7 w-7 items-center justify-center rounded-full border border-border/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                color === c && 'ring-2 ring-ring ring-offset-1',
              )}
              style={{ backgroundColor: c }}
            >
              {color === c && <Check className="h-3.5 w-3.5 text-linen" />}
            </button>
          ))}
          <button
            type="button"
            onClick={() => {
              onChange(undefined);
              setOpen(false);
            }}
            className="flex h-7 w-7 items-center justify-center rounded-full border border-dashed border-muted-foreground/60 text-[10px] text-muted-foreground hover:bg-secondary"
            aria-label="Automatic colour"
          >
            auto
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ---------------------------------------------------------------------------
// Per-kind forms
// ---------------------------------------------------------------------------

function MetricForm({ draft, update }: { draft: MetricWidget; update: (patch: Partial<MetricWidget>) => void }) {
  const { catalog } = useDashboard();
  const def = catalog.metric(draft.metric);
  const unit = def?.unit ?? 'count';
  const [targetText, setTargetText] = useState(draft.target != null ? String(naturalToDisplay(draft.target, unit)) : '');
  const suffix = unitInputSuffix(unit);
  const noHistory = def?.grain === 'snapshot' || def?.grain === 'window';

  return (
    <>
      <Field label="Metric" htmlFor="metric-source" hint={def?.description}>
        <SourcePicker
          id="metric-source"
          kind="metric"
          value={draft.metric || null}
          onChange={(id) => {
            update({ metric: id, target: null });
            setTargetText('');
          }}
        />
      </Field>
      <SectionTitle>Display</SectionTitle>
      <SwitchRow
        label="Show change vs. prior period"
        hint={def?.grain === 'snapshot' ? 'Snapshot metrics describe right now, so there is no prior period.' : null}
        checked={draft.showDelta}
        disabled={def?.grain === 'snapshot'}
        onCheckedChange={(v) => update({ showDelta: v })}
      />
      <SwitchRow
        label="Show sparkline"
        hint={noHistory ? 'This metric has no daily history to draw.' : 'Needs at least two rows of height.'}
        checked={draft.showSparkline}
        disabled={noHistory}
        onCheckedChange={(v) => update({ showSparkline: v })}
      />
      <Field label="Target" hint="Shows progress toward the target for the selected range. Leave blank for none.">
        <div className="relative">
          <Input
            type="number"
            inputMode="decimal"
            step="any"
            value={targetText}
            placeholder="None"
            disabled={!def}
            onChange={(e) => {
              setTargetText(e.target.value);
              const parsed = Number(e.target.value);
              update({ target: e.target.value.trim() === '' || !Number.isFinite(parsed) ? null : displayToNatural(parsed, unit) });
            }}
            className={cn(suffix && 'pr-14')}
          />
          {suffix && <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs text-muted-foreground">{suffix}</span>}
        </div>
      </Field>
    </>
  );
}

function ChartForm({ draft, update }: { draft: ChartWidget; update: (patch: Partial<ChartWidget>) => void }) {
  const { catalog } = useDashboard();
  const breakdownMode = isBreakdownChart(draft.chartType);
  const usedMetrics = draft.series.map((s) => s.metric);
  const availableMetrics = catalog.metrics.filter((m) => !usedMetrics.includes(m.id));

  const unitWarning = useMemo(() => {
    if (breakdownMode || draft.chartType === 'combo') return null;
    const units = new Set(draft.series.map((s) => catalog.metric(s.metric)?.unit).filter(Boolean));
    return units.size > 1 ? 'These metrics use different units. A "Bars + line" chart can put one of them on a right-hand axis.' : null;
  }, [breakdownMode, draft.chartType, draft.series, catalog]);

  const setSeries = (index: number, patch: Partial<ChartWidget['series'][number]>) =>
    update({ series: draft.series.map((s, i) => (i === index ? { ...s, ...patch } : s)) });

  return (
    <>
      <Field label="Chart type" htmlFor="chart-type">
        <Select value={draft.chartType} onValueChange={(v) => update({ chartType: v as ChartType })}>
          <SelectTrigger id="chart-type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectLabel className="text-[10px] uppercase tracking-wide">Over time</SelectLabel>
              {TIME_SERIES_TYPES.map((t) => (
                <SelectItem key={t} value={t}>
                  {CHART_TYPE_LABELS[t]}
                </SelectItem>
              ))}
            </SelectGroup>
            <SelectGroup>
              <SelectLabel className="text-[10px] uppercase tracking-wide">By category</SelectLabel>
              {BREAKDOWN_TYPES.map((t) => (
                <SelectItem key={t} value={t}>
                  {CHART_TYPE_LABELS[t]}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </Field>

      {breakdownMode ? (
        <>
          <Field label="Breakdown" htmlFor="chart-breakdown" hint={draft.breakdown ? catalog.byId.get(draft.breakdown)?.description : 'Totals for the selected range, split by category.'}>
            <SourcePicker id="chart-breakdown" kind="breakdown" value={draft.breakdown ?? null} onChange={(id) => update({ breakdown: id })} />
          </Field>
          <Field label="Slices shown" hint="Smaller categories fold into “Other”.">
            <Select value={String(draft.limit ?? 8)} onValueChange={(v) => update({ limit: Number(v) })}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[5, 8, 10, 15, 20].map((n) => (
                  <SelectItem key={n} value={String(n)}>
                    Top {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </>
      ) : (
        <>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-medium text-muted-foreground">Series</Label>
              <span className="text-[11px] text-muted-foreground">
                {draft.series.length}/{MAX_SERIES}
              </span>
            </div>
            {draft.series.length === 0 && (
              <p className="rounded-lg border border-dashed border-border px-3 py-3 text-center text-xs text-muted-foreground">Add at least one metric to plot.</p>
            )}
            {draft.series.map((s, index) => (
              <div key={`${s.metric}-${index}`} className="flex flex-wrap items-center gap-2">
                <ColorSwatch color={s.color} onChange={(color) => setSeries(index, { color })} />
                <div className="min-w-[160px] flex-1">
                  <SourcePicker compact kind="metric" value={s.metric || null} exclude={usedMetrics.filter((m) => m !== s.metric)} onChange={(id) => setSeries(index, { metric: id })} />
                </div>
                {draft.chartType === 'combo' && (
                  <>
                    <Select value={s.kind ?? 'bar'} onValueChange={(v) => setSeries(index, { kind: v as 'bar' | 'line' | 'area' })}>
                      <SelectTrigger className="h-9 w-[92px]" aria-label="Series style">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="bar">Bars</SelectItem>
                        <SelectItem value="line">Line</SelectItem>
                        <SelectItem value="area">Area</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={s.axis ?? 'left'} onValueChange={(v) => setSeries(index, { axis: v as 'left' | 'right' })}>
                      <SelectTrigger className="h-9 w-[112px]" aria-label="Axis">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="left">Left axis</SelectItem>
                        <SelectItem value="right">Right axis</SelectItem>
                      </SelectContent>
                    </Select>
                  </>
                )}
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 shrink-0 text-muted-foreground"
                  aria-label="Remove series"
                  onClick={() => update({ series: draft.series.filter((_, i) => i !== index) })}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={draft.series.length >= MAX_SERIES || availableMetrics.length === 0}
              onClick={() => update({ series: [...draft.series, { metric: availableMetrics[0]?.id ?? '' }] })}
            >
              <Plus className="h-4 w-4" /> Add series
            </Button>
            {unitWarning && <p className="text-xs text-fresh-salmon-dark">{unitWarning}</p>}
          </div>

          <Field label="Time grain" hint="Auto picks days, weeks or months to suit the selected range.">
            <Select value={draft.grain} onValueChange={(v) => update({ grain: v as ChartWidget['grain'] })}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Auto</SelectItem>
                <SelectItem value="day">Daily</SelectItem>
                <SelectItem value="week">Weekly</SelectItem>
                <SelectItem value="month">Monthly</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <SectionTitle>Options</SectionTitle>
          <SwitchRow label="Compare with prior period" hint="Overlays the previous window as dashed lines." checked={draft.compare} onCheckedChange={(v) => update({ compare: v })} />
          <SwitchRow label="Cumulative" hint="Running total across the range — useful for pacing against a goal." checked={draft.cumulative} onCheckedChange={(v) => update({ cumulative: v })} />
        </>
      )}
      <SwitchRow label="Show legend" checked={draft.showLegend} onCheckedChange={(v) => update({ showLegend: v })} />
    </>
  );
}

function ItemPicker({ value, onChange }: { value: string | null; onChange: (id: string | null) => void }) {
  const { window, isMobile } = useDashboard();
  const items = useQuery(datasetQueryOptions('items', window));
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const list: ItemLite[] = useMemo(() => {
    const all = (items.data ?? []).filter((i) => i.is_active || i.id === value);
    const needle = query.trim().toLowerCase();
    return needle ? all.filter((i) => `${i.sku} ${i.name}`.toLowerCase().includes(needle)) : all;
  }, [items.data, query, value]);
  const selected = items.data?.find((i) => i.id === value);
  const label = selected ? `${selected.sku} · ${selected.name}` : items.isLoading ? 'Loading items…' : 'Choose an item';

  if (isMobile) {
    return (
      <select
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value || null)}
        className="flex h-10 w-full rounded-lg border border-input bg-card px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <option value="">{items.isLoading ? 'Loading items…' : 'Choose an item'}</option>
        {(items.data ?? []).map((i) => (
          <option key={i.id} value={i.id}>
            {i.sku} · {i.name}
          </option>
        ))}
      </select>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          className={cn(
            'flex h-10 w-full items-center justify-between gap-2 rounded-lg border border-input bg-card px-3 text-left text-sm hover:bg-secondary/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            !selected && 'text-muted-foreground',
          )}
        >
          <span className="truncate">{label}</span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] min-w-[320px] p-0">
        <div className="flex items-center gap-2 border-b border-border px-3">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by SKU or name…"
            className="h-10 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          />
        </div>
        <div role="listbox" className="max-h-[320px] overflow-y-auto p-1">
          {list.length === 0 && <p className="px-3 py-6 text-center text-sm text-muted-foreground">{items.isLoading ? 'Loading…' : 'No items match.'}</p>}
          {list.slice(0, 200).map((i) => (
            <button
              key={i.id}
              type="button"
              role="option"
              aria-selected={i.id === value}
              onClick={() => {
                onChange(i.id);
                setOpen(false);
                setQuery('');
              }}
              className={cn('flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-secondary', i.id === value && 'bg-secondary/70')}
            >
              <Check className={cn('h-4 w-4 shrink-0', i.id === value ? 'opacity-100' : 'opacity-0')} />
              <span className="shrink-0 font-mono text-xs text-muted-foreground">{i.sku}</span>
              <span className="truncate">{i.name}</span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function ForecastForm({ draft, update }: { draft: ForecastWidget; update: (patch: Partial<ForecastWidget>) => void }) {
  const { catalog } = useDashboard();
  const def = catalog.byId.get(draft.source);
  const source: ForecastSourceDef | null = def && def.kind === 'forecast' ? def : null;

  return (
    <>
      <Field label="Series to forecast" htmlFor="forecast-source" hint={source?.description}>
        <SourcePicker
          id="forecast-source"
          kind="forecast"
          value={draft.source || null}
          onChange={(id) => {
            const next = catalog.byId.get(id);
            update({ source: id, itemId: next && next.kind === 'forecast' && next.perItem ? draft.itemId : null });
          }}
        />
      </Field>
      {source?.perItem && (
        <Field label="Item" hint="Demand is forecast per catalog item.">
          <ItemPicker value={draft.itemId ?? null} onChange={(id) => update({ itemId: id })} />
        </Field>
      )}
      <Field label="Model" htmlFor="forecast-method" hint={draft.method === 'auto' ? 'Backtests every model on recent months and keeps the most accurate.' : draft.method === 'seasonal' ? 'Needs at least two full years of history; falls back to Holt otherwise.' : undefined}>
        <Select value={draft.method} onValueChange={(v) => update({ method: v as ForecastMethod })}>
          <SelectTrigger id="forecast-method">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(Object.keys(FORECAST_METHOD_LABELS) as ForecastMethod[]).map((m) => (
              <SelectItem key={m} value={m}>
                {FORECAST_METHOD_LABELS[m]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Horizon">
          <Select value={String(draft.horizon)} onValueChange={(v) => update({ horizon: Number(v) })}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="0">Page setting</SelectItem>
              <SelectItem value="3">3 months</SelectItem>
              <SelectItem value="6">6 months</SelectItem>
              <SelectItem value="12">12 months</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="History shown">
          <Select value={String(draft.history)} onValueChange={(v) => update({ history: Number(v) })}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="0">Page setting</SelectItem>
              <SelectItem value="6">6 months</SelectItem>
              <SelectItem value="12">12 months</SelectItem>
              <SelectItem value="24">24 months</SelectItem>
            </SelectContent>
          </Select>
        </Field>
      </div>
      <SectionTitle>Overlays</SectionTitle>
      <SwitchRow label="Show confidence band" hint="Shaded ~80% range that widens with the horizon." checked={draft.showBand} onCheckedChange={(v) => update({ showBand: v })} />
      {source?.manual && (
        <SwitchRow label={`Show ${source.manualLabel?.toLowerCase() ?? 'plan'}`} hint="Dashed line from the projections you have entered." checked={draft.showManual} onCheckedChange={(v) => update({ showManual: v })} />
      )}
    </>
  );
}

function TableForm({ draft, update }: { draft: TableWidget; update: (patch: Partial<TableWidget>) => void }) {
  const { catalog } = useDashboard();
  const def = catalog.byId.get(draft.table);
  const table: TableDef | null = def && def.kind === 'table' ? def : null;
  const allKeys = table?.columns.map((c) => c.key) ?? [];
  const visible = draft.columns.length > 0 ? draft.columns : allKeys;

  const toggleColumn = (key: string) => {
    if (!table) return;
    const set = new Set(visible);
    if (set.has(key)) {
      if (set.size === 1) return;
      set.delete(key);
    } else {
      set.add(key);
    }
    const ordered = allKeys.filter((k) => set.has(k));
    update({ columns: ordered.length === allKeys.length ? [] : ordered });
  };

  return (
    <>
      <Field label="Table" htmlFor="table-source" hint={table?.description}>
        <SourcePicker
          id="table-source"
          kind="table"
          value={draft.table || null}
          onChange={(id) => update({ table: id, columns: [], sort: null })}
        />
      </Field>
      {table && (
        <div className="space-y-1.5">
          <Label className="text-xs font-medium text-muted-foreground">Columns</Label>
          <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 rounded-lg border border-border p-3">
            {table.columns.map((c) => {
              const checked = visible.includes(c.key);
              return (
                <label key={c.key} className="flex cursor-pointer items-center gap-2 text-sm">
                  <Checkbox checked={checked} onCheckedChange={() => toggleColumn(c.key)} ariaLabel={c.label} />
                  <span className="truncate">{c.label}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <Field label="Rows">
          <Select value={String(draft.limit)} onValueChange={(v) => update({ limit: Number(v) })}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="0">Default{table?.defaultLimit ? ` (${table.defaultLimit})` : ''}</SelectItem>
              {[5, 10, 20, 50].map((n) => (
                <SelectItem key={n} value={String(n)}>
                  {n} rows
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Sort by">
          <div className="flex gap-1.5">
            <Select
              value={draft.sort?.key ?? '__default'}
              onValueChange={(v) => update({ sort: v === '__default' ? null : { key: v, dir: draft.sort?.dir ?? 'desc' } })}
              disabled={!table}
            >
              <SelectTrigger className="min-w-0 flex-1">
                <SelectValue placeholder="Default" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__default">Default</SelectItem>
                {table?.columns.map((c) => (
                  <SelectItem key={c.key} value={c.key}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-10 w-10 shrink-0"
              disabled={!draft.sort}
              aria-label={draft.sort?.dir === 'asc' ? 'Ascending — switch to descending' : 'Descending — switch to ascending'}
              onClick={() => draft.sort && update({ sort: { ...draft.sort, dir: draft.sort.dir === 'asc' ? 'desc' : 'asc' } })}
            >
              {draft.sort?.dir === 'asc' ? <ArrowUpAZ className="h-4 w-4" /> : <ArrowDownAZ className="h-4 w-4" />}
            </Button>
          </div>
        </Field>
      </div>
    </>
  );
}

function TextForm({ draft, update }: { draft: TextWidget; update: (patch: Partial<TextWidget>) => void }) {
  return (
    <Field label="Note" htmlFor="text-body" hint="Blank lines start a new paragraph.">
      <Textarea id="text-body" rows={6} value={draft.body} onChange={(e) => update({ body: e.target.value })} placeholder="What should the team know when they look at this section?" />
    </Field>
  );
}

// ---------------------------------------------------------------------------
// Validation + shell
// ---------------------------------------------------------------------------

function validate(draft: DashboardWidget, lookup: (id: string) => unknown): string | null {
  switch (draft.kind) {
    case 'metric':
      return draft.metric ? null : 'Choose a metric.';
    case 'chart':
      if (isBreakdownChart(draft.chartType)) return draft.breakdown ? null : 'Choose a breakdown.';
      if (draft.series.length === 0) return 'Add at least one series.';
      return draft.series.every((s) => s.metric) ? null : 'Every series needs a metric.';
    case 'forecast': {
      if (!draft.source) return 'Choose a series to forecast.';
      const def = lookup(draft.source) as ForecastSourceDef | MetricDef | undefined;
      if (def && def.kind === 'forecast' && def.perItem && !draft.itemId) return 'Choose an item.';
      return null;
    }
    case 'table':
      return draft.table ? null : 'Choose a table.';
    case 'text':
      return draft.body.trim() || draft.title?.trim() ? null : 'Write something in the note.';
  }
}

function previewHeight(widget: DashboardWidget): number {
  if (widget.kind === 'metric') return widget.h >= 2 ? 150 : 110;
  if (widget.kind === 'text') return 120;
  return 260;
}

function WidgetForm({
  seed,
  isNew,
  onSave,
  onRemove,
  onClose,
  onDraftChange,
}: {
  seed: DashboardWidget;
  isNew: boolean;
  onSave: (widget: DashboardWidget) => void;
  onRemove: (widgetId: string) => void;
  onClose: () => void;
  onDraftChange?: (draft: DashboardWidget) => void;
}) {
  const { catalog, isMobile } = useDashboard();
  const [draft, setDraft] = useState<DashboardWidget>(seed);
  const [titleText, setTitleText] = useState(seed.title ?? '');

  useEffect(() => {
    onDraftChange?.(draft);
  }, [draft, onDraftChange]);

  const update = (patch: Partial<DashboardWidget>) => setDraft((prev) => ({ ...prev, ...patch }) as DashboardWidget);
  const kindLabel = WIDGET_KIND_LABELS[draft.kind];
  const preview = useMemo<DashboardWidget>(() => ({ ...draft, title: titleText.trim() || undefined }), [draft, titleText]);
  const problem = validate(preview, (id) => catalog.byId.get(id));

  const body = (
    <div className="space-y-4">
      {!isMobile && (
        <div className="space-y-1.5">
          <Label className="text-xs font-medium text-muted-foreground">Preview</Label>
          <div className="rounded-lg border border-border bg-card p-4" style={{ height: previewHeight(preview) }}>
            {problem ? (
              <div className="flex h-full items-center justify-center text-center text-sm text-muted-foreground">{problem}</div>
            ) : (
              <WidgetBody widget={preview} />
            )}
          </div>
        </div>
      )}
      <Field label="Title" htmlFor="widget-title" hint={draft.kind === 'text' ? undefined : 'Leave blank to use the source name.'}>
        <Input id="widget-title" value={titleText} onChange={(e) => setTitleText(e.target.value)} placeholder={draft.kind === 'text' ? 'Optional heading' : 'Use source name'} />
      </Field>
      {draft.kind === 'metric' && <MetricForm draft={draft} update={update} />}
      {draft.kind === 'chart' && <ChartForm draft={draft} update={update} />}
      {draft.kind === 'forecast' && <ForecastForm draft={draft} update={update} />}
      {draft.kind === 'table' && <TableForm draft={draft} update={update} />}
      {draft.kind === 'text' && <TextForm draft={draft} update={update} />}
      <SectionTitle>Size</SectionTitle>
      <SizeFields widget={draft} onChange={(patch) => update(patch)} />
    </div>
  );

  const footer = (
    <>
      {!isNew && (
        <Button type="button" variant="ghost" className="text-fresh-salmon-dark hover:text-fresh-salmon-dark sm:mr-auto" onClick={() => onRemove(draft.id)}>
          <Trash2 className="h-4 w-4" /> Remove
        </Button>
      )}
      <Button type="button" variant="outline" onClick={onClose}>
        Cancel
      </Button>
      <Button type="button" disabled={!!problem} title={problem ?? undefined} onClick={() => onSave(preview)}>
        {isNew ? `Add ${kindLabel.toLowerCase()}` : 'Save changes'}
      </Button>
    </>
  );

  const title = isNew ? `New ${kindLabel.toLowerCase()}` : `Edit ${kindLabel.toLowerCase()}`;

  if (isMobile) {
    return (
      <>
        <BottomSheetHeader title={title} description={KIND_HINTS[draft.kind]} />
        <BottomSheetBody>{body}</BottomSheetBody>
        <BottomSheetFooter>{footer}</BottomSheetFooter>
      </>
    );
  }
  return (
    <>
      <SheetHeader>
        <SheetTitle>{title}</SheetTitle>
        <SheetDescription>{KIND_HINTS[draft.kind]}</SheetDescription>
      </SheetHeader>
      <SheetBody>{body}</SheetBody>
      <SheetFooter>{footer}</SheetFooter>
    </>
  );
}

export function WidgetEditor({ open, widget, isNew, onSave, onRemove, onClose, onDraftChange }: WidgetEditorProps) {
  const { isMobile } = useDashboard();
  const handleOpenChange = (next: boolean) => {
    if (!next) onClose();
  };

  if (isMobile) {
    return (
      <BottomSheet open={open && !!widget} onOpenChange={handleOpenChange}>
        {widget && <WidgetForm key={widget.id} seed={widget} isNew={isNew} onSave={onSave} onRemove={onRemove} onClose={onClose} onDraftChange={onDraftChange} />}
      </BottomSheet>
    );
  }
  return (
    <Sheet open={open && !!widget} onOpenChange={handleOpenChange}>
      <SheetContent className="sm:max-w-2xl" dismissOnOutsideClick={false}>
        {widget && <WidgetForm key={widget.id} seed={widget} isNew={isNew} onSave={onSave} onRemove={onRemove} onClose={onClose} onDraftChange={onDraftChange} />}
      </SheetContent>
    </Sheet>
  );
}
